import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { parseEmail } from "@/services/inbound-email.service";
import { Field, textareaClass } from "@/components/workspace-settings/email-to-task/fields";

/** Paste an email, run the agent, then review the draft in the Inbox like any inbound email. */
export function EmailParserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
	const navigate = useNavigate();
	const { mutate } = useSWRConfig();
	const [sender, setSender] = useState("");
	const [subject, setSubject] = useState("");
	const [text, setText] = useState("");
	const [running, setRunning] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setRunning(true);
		try {
			const email = await parseEmail({ text, subject: subject || undefined, sender: sender || undefined });
			mutate(["inbound-emails", "ready"]);
			toast.success("Draft ready for review.");
			setSender("");
			setSubject("");
			setText("");
			onOpenChange(false);
			navigate(`/inbox/${email.id}?tab=ready`);
		} catch (err) {
			toast.error((err as Error).message || "Could not parse the email.");
		} finally {
			setRunning(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Email Parser</DialogTitle>
					<DialogDescription>
						Paste an email to draft tasks from it. The draft opens here for review; nothing is created until you approve.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="space-y-3">
					<div className="grid gap-3 sm:grid-cols-2">
						<Field
							label="Original sender (optional)"
							htmlFor="email-parser-sender"
							hint={'So "me" and "I" resolve to them. Defaults to you.'}
						>
							<Input
								id="email-parser-sender"
								placeholder="Name <name@example.com>"
								value={sender}
								onChange={(e) => setSender(e.target.value)}
								disabled={running}
							/>
						</Field>
						<Field label="Subject (optional)" htmlFor="email-parser-subject">
							<Input
								id="email-parser-subject"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								disabled={running}
							/>
						</Field>
					</div>
					<Field label="Email body" htmlFor="email-parser-body">
						<textarea
							id="email-parser-body"
							rows={12}
							className={textareaClass}
							placeholder="Paste the email text here"
							value={text}
							onChange={(e) => setText(e.target.value)}
							disabled={running}
							required
						/>
					</Field>
					<DialogFooter className="items-center">
						{running && <p className="mr-auto text-xs text-muted">The agent can take up to a minute.</p>}
						<Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={running}>
							Cancel
						</Button>
						<Button type="submit" size="sm" disabled={running || !text.trim()}>
							{running ? "Parsing..." : "Parse email"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
